import React, {useMemo, useState} from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import useNotification from '../../../utility/hooks/useNotification';
import {
    useCreateFormTemplateCategoryMutation,
    useDeleteFormTemplateCategoryMutation,
    useGetFormTemplateCategoriesQuery,
    useRenameFormTemplateCategoryMutation
} from '../../../api/services/formTemplateLibraryService';

const TemplateCategories = () => {
    const {showErrorNotification, showSuccessNotification} = useNotification();
    const {data: categories = []} = useGetFormTemplateCategoriesQuery();
    const [createCategory] = useCreateFormTemplateCategoryMutation();
    const [renameCategory] = useRenameFormTemplateCategoryMutation();
    const [deleteCategory] = useDeleteFormTemplateCategoryMutation();

    const [addOpen, setAddOpen] = useState(false);
    const [newName, setNewName] = useState('');
    const [newParentId, setNewParentId] = useState('');
    const [renaming, setRenaming] = useState(null);
    const [renameValue, setRenameValue] = useState('');

    const tree = useMemo(() => {
        const parents = categories.filter(category => !category.parentId);

        return parents.map(parent => ({
            ...parent,
            children: categories.filter(category => category.parentId === parent.id)
        }));
    }, [categories]);

    const add = async () => {
        if (newName.trim().length === 0) {
            showErrorNotification('A name is required');
            return;
        }

        const result = await createCategory({
            name: newName.trim(),
            parentId: newParentId === '' ? null : Number(newParentId)
        });

        if (result.error) {
            showErrorNotification(result.error.message ?? 'Failed to create the category');
            return;
        }

        showSuccessNotification('Category created');
        setAddOpen(false);
        setNewName('');
        setNewParentId('');
    };

    const rename = async () => {
        if (renameValue.trim().length === 0) {
            showErrorNotification('A name is required');
            return;
        }

        const result = await renameCategory({categoryId: renaming.id, name: renameValue.trim()});

        if (result.error) {
            showErrorNotification(result.error.message ?? 'Failed to rename the category');
            return;
        }

        showSuccessNotification('Category renamed');
        setRenaming(null);
    };

    const remove = async (category) => {
        const result = await deleteCategory(category.id);

        if (result.error) {
            showErrorNotification(result.error.message ?? 'Failed to delete the category');
            return;
        }

        showSuccessNotification(`${category.name} has been deleted`);
    };

    const row = (category, isChild) => (
        <Stack
            key={category.id}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            data-testid={`category-row-${category.id}`}
            sx={{
                py: 1,
                pl: isChild ? 4 : 0,
                borderBottom: '1px solid #e5e7eb'
            }}
        >
            <Typography fontWeight={isChild ? 400 : 600}>
                {category.name}
            </Typography>
            <Stack direction="row" alignItems="center" gap={2}>
                <Typography variant="body2" color="text.secondary">
                    {category.templateCount} templates
                </Typography>
                <IconButton
                    size="small"
                    aria-label={`Rename ${category.name}`}
                    data-testid={`rename-category-${category.id}`}
                    onClick={() => {
                        setRenaming(category);
                        setRenameValue(category.name);
                    }}
                >
                    <EditOutlinedIcon fontSize="small"/>
                </IconButton>
                <IconButton
                    size="small"
                    aria-label={`Delete ${category.name}`}
                    data-testid={`delete-category-${category.id}`}
                    onClick={() => remove(category)}
                >
                    <DeleteOutlinedIcon fontSize="small"/>
                </IconButton>
            </Stack>
        </Stack>
    );

    return (
        <Box>
            <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon/>}
                sx={{mb: 3}}
                data-testid="add-category"
                onClick={() => setAddOpen(true)}
            >
                Add a category
            </Button>

            {tree.map(parent => (
                <Box key={parent.id} sx={{mb: 1}}>
                    {row(parent, false)}
                    {parent.children.map(child => row(child, true))}
                </Box>
            ))}

            <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth>
                <DialogTitle>Add a category</DialogTitle>
                <DialogContent sx={{pt: 1}}>
                    <Stack gap={2} sx={{mt: 1}}>
                        <TextField
                            fullWidth
                            required
                            size="small"
                            label="Name"
                            data-testid="new-category-name"
                            value={newName}
                            onChange={(event) => setNewName(event.target.value)}
                        />
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Under"
                            data-testid="new-category-parent"
                            value={newParentId}
                            onChange={(event) => setNewParentId(event.target.value)}
                        >
                            <MenuItem value="">Nothing, it stands on its own</MenuItem>
                            {categories.filter(category => !category.parentId).map(category => (
                                <MenuItem key={category.id} value={String(category.id)}>
                                    {category.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddOpen(false)}>Cancel</Button>
                    <Button variant="contained" data-testid="save-new-category" onClick={add}>Add</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(renaming)} onClose={() => setRenaming(null)} fullWidth>
                <DialogTitle>Rename category</DialogTitle>
                <DialogContent sx={{pt: 1}}>
                    <TextField
                        fullWidth
                        required
                        size="small"
                        label="Name"
                        sx={{mt: 1}}
                        data-testid="rename-category-name"
                        value={renameValue}
                        onChange={(event) => setRenameValue(event.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRenaming(null)}>Cancel</Button>
                    <Button variant="contained" data-testid="save-renamed-category" onClick={rename}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TemplateCategories;
